import React, { useEffect, useState, useCallback } from 'react';
import { intakeFormService } from '../services/api';
import { formatDate } from '../services/dateUtils';
import '../styles/IntakeFormPanel.css';

// Investigator-facing "Intake" tab on Investigation Detail (Aug 18 2026).
// Same general tab-panel pattern as InvestigationStatementsPanel.js. Two
// distinct modes baked into one component:
//   - status === 'pending': client hasn't submitted yet -- show the shareable
//     link and nothing else (there's no data to show).
//   - status === 'submitted' / 'reviewed': full read/edit view of everything
//     the client entered, plus investigator-only notes below it.
// See models.IntakeForm and routers/intake_forms.py for the full lifecycle.

const STATUS_META = {
  pending:   { label: 'Awaiting client', cls: 'pending' },
  submitted: { label: 'Submitted',       cls: 'submitted' },
  reviewed:  { label: 'Reviewed',        cls: 'reviewed' },
};

function fmt(ts) {
  if (!ts) return '—';
  try { return formatDate(ts); } catch { return String(ts); }
}

function toDateInput(v) {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
}

function Row({ label, children }) {
  return (
    <div className="intakepanel-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

export default function IntakeFormPanel({ investigationId }) {
  const [form, setForm]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [edits, setEdits]         = useState({});

  const [notes, setNotes]         = useState([]);
  const [noteText, setNoteText]   = useState('');
  const [notesLoading, setNotesLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await intakeFormService.get(investigationId);
      setForm(res.data);
      setEdits({});
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load intake form');
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  const loadNotes = useCallback(async () => {
    try {
      setNotesLoading(true);
      const res = await intakeFormService.getNotes(investigationId);
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load intake notes:', e.response?.data || e.message);
    } finally {
      setNotesLoading(false);
    }
  }, [investigationId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (form && form.status !== 'pending') loadNotes(); }, [form, loadNotes]);

  const clientLink = form ? `${window.location.origin}/intake/${form.client_token}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(clientLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', clientLink);
    }
  };

  const set = (key, value) => setEdits(e => ({ ...e, [key]: value }));
  const val = (key) => (key in edits ? edits[key] : form?.[key] ?? '');

  const handleSave = async (statusOverride = null) => {
    try {
      setSaving(true);
      setSaveMsg(null);
      const payload = { ...edits };
      if (statusOverride) payload.status = statusOverride;
      const res = await intakeFormService.update(investigationId, payload);
      setForm(res.data);
      setEdits({});
      setSaveMsg('✓ Saved');
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (e) {
      setSaveMsg(null);
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await intakeFormService.addNote(investigationId, noteText.trim());
      setNoteText('');
      await loadNotes();
    } catch (e) {
      alert('Failed to add note: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await intakeFormService.deleteNote(investigationId, noteId);
      await loadNotes();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="intakepanel-loading">Loading…</div>;
  if (error && !form) return <div className="intakepanel-error">⚠ {error}</div>;
  if (!form) return null;

  const meta = STATUS_META[form.status] || STATUS_META.pending;
  const hasEdits = Object.keys(edits).length > 0;

  return (
    <div className="intakepanel">
      <div className="intakepanel-header">
        <h2>📋 Client Intake Form</h2>
        <span className={`intakepanel-status ${meta.cls}`}>{meta.label}</span>
      </div>

      {/* ── Shareable link -- always visible regardless of status ── */}
      <div className="intakepanel-link-box">
        <div>
          <div className="intakepanel-link-label">Client link</div>
          <code className="intakepanel-link-value">{clientLink}</code>
        </div>
        <button className="intakepanel-btn-secondary" onClick={copyLink}>
          {linkCopied ? '✓ Copied' : '🔗 Copy client link'}
        </button>
      </div>

      {error && <div className="intakepanel-error">⚠ {error}</div>}

      {form.status === 'pending' ? (
        <div className="intakepanel-empty">
          The client hasn't submitted this form yet. Share the link above with them — once they
          submit, their answers will appear here.
        </div>
      ) : (
        <>
          <div className="intakepanel-form">
            <section className="intakepanel-section">
              <h3>Contact Information</h3>
              <div className="intakepanel-grid-2">
                <Row label="Full name"><input type="text" value={val('client_name') || ''} onChange={e => set('client_name', e.target.value)} /></Row>
                <Row label="Email"><input type="email" value={val('client_email') || ''} onChange={e => set('client_email', e.target.value)} /></Row>
                <Row label="Phone"><input type="tel" value={val('client_phone') || ''} onChange={e => set('client_phone', e.target.value)} /></Row>
                <Row label="Country"><input type="text" value={val('client_country') || ''} onChange={e => set('client_country', e.target.value)} /></Row>
              </div>
            </section>

            <section className="intakepanel-section">
              <h3>Incident Overview</h3>
              <div className="intakepanel-grid-2">
                <Row label="Scam start date"><input type="date" value={toDateInput(val('scam_start_date'))} onChange={e => set('scam_start_date', e.target.value)} /></Row>
                <Row label="Scam end date"><input type="date" value={toDateInput(val('scam_end_date'))} onChange={e => set('scam_end_date', e.target.value)} /></Row>
                <Row label="Total loss amount"><input type="number" step="any" value={val('total_loss_amount') ?? ''} onChange={e => set('total_loss_amount', e.target.value)} /></Row>
                <Row label="Currency"><input type="text" value={val('total_loss_currency') || ''} onChange={e => set('total_loss_currency', e.target.value)} /></Row>
                <Row label="Fake platform name"><input type="text" value={val('fake_platform_name') || ''} onChange={e => set('fake_platform_name', e.target.value)} /></Row>
                <Row label="Initial contact method"><input type="text" value={val('initial_contact_method') || ''} onChange={e => set('initial_contact_method', e.target.value)} /></Row>
              </div>
              <Row label="Scammer username(s)">
                <textarea rows={2} value={val('scammer_usernames') || ''} onChange={e => set('scammer_usernames', e.target.value)} />
              </Row>
            </section>

            <section className="intakepanel-section">
              <h3>Law Enforcement & Official Reports</h3>
              <div className="intakepanel-grid-2">
                <Row label="Police report filed">
                  <select value={val('police_report_filed') === true || val('police_report_filed') === 'true' ? 'yes' : 'no'}
                    onChange={e => set('police_report_filed', e.target.value === 'yes')}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Row>
                <Row label="Police department"><input type="text" value={val('police_department') || ''} onChange={e => set('police_department', e.target.value)} /></Row>
                <Row label="Case number"><input type="text" value={val('police_case_number') || ''} onChange={e => set('police_case_number', e.target.value)} /></Row>
                <Row label="Report date"><input type="date" value={toDateInput(val('police_report_date'))} onChange={e => set('police_report_date', e.target.value)} /></Row>
                <Row label="Cybercrime reference"><input type="text" value={val('cybercrime_reference') || ''} onChange={e => set('cybercrime_reference', e.target.value)} /></Row>
                <Row label="Assigned officer"><input type="text" value={val('assigned_officer_name') || ''} onChange={e => set('assigned_officer_name', e.target.value)} /></Row>
              </div>
            </section>

            <section className="intakepanel-section">
              <h3>Narrative</h3>
              <textarea rows={6} className="intakepanel-narrative"
                value={val('narrative') || ''} onChange={e => set('narrative', e.target.value)} />
            </section>

            <section className="intakepanel-section">
              <h3>Evidence Checklist</h3>
              <div className="intakepanel-evidence-grid">
                <div className="intakepanel-evidence-cell">
                  <span className={`intakepanel-pill ${form.evidence_identity_docs ? 'ok' : 'muted'}`}>
                    {form.evidence_identity_docs ? '✓ Identity documents' : '— Identity documents'}
                  </span>
                  {form.evidence_identity_notes && <p>{form.evidence_identity_notes}</p>}
                </div>
                <div className="intakepanel-evidence-cell">
                  <span className={`intakepanel-pill ${form.evidence_blockchain ? 'ok' : 'muted'}`}>
                    {form.evidence_blockchain ? '✓ Blockchain evidence' : '— Blockchain evidence'}
                  </span>
                  {form.evidence_blockchain && (
                    <div className="intakepanel-blockchain-evidence">
                      {form.evidence_txids && <div><label>TXIDs</label><pre>{form.evidence_txids}</pre></div>}
                      {form.evidence_scammer_addresses && <div><label>Scammer addresses</label><pre>{form.evidence_scammer_addresses}</pre></div>}
                      {form.evidence_client_addresses && <div><label>Client addresses</label><pre>{form.evidence_client_addresses}</pre></div>}
                      <p className="intakepanel-hint">
                        Import into Seed TXs isn't wired up yet — for now, copy TXIDs across manually
                        on the Seed TXs tab.
                      </p>
                    </div>
                  )}
                </div>
                <div className="intakepanel-evidence-cell">
                  <span className={`intakepanel-pill ${form.evidence_communications ? 'ok' : 'muted'}`}>
                    {form.evidence_communications ? '✓ Communications' : '— Communications'}
                  </span>
                  {form.evidence_communications_notes && <p>{form.evidence_communications_notes}</p>}
                </div>
                <div className="intakepanel-evidence-cell">
                  <span className={`intakepanel-pill ${form.evidence_financial_proof ? 'ok' : 'muted'}`}>
                    {form.evidence_financial_proof ? '✓ Proof of payment' : '— Proof of payment'}
                  </span>
                  {form.evidence_financial_notes && <p>{form.evidence_financial_notes}</p>}
                </div>
              </div>
            </section>

            <section className="intakepanel-section">
              <h3>Disclaimer & Signature</h3>
              <div className="intakepanel-grid-2">
                <Row label="Disclaimer accepted"><span>{form.disclaimer_accepted ? '✓ Yes' : '✗ No'}</span></Row>
                <Row label="Signed by"><span>{form.signature_name || '—'}</span></Row>
              </div>
            </section>

            <div className="intakepanel-meta-footer">
              <span>Submitted {fmt(form.submitted_at)}</span>
              {form.reviewed_at && <span>· Reviewed {fmt(form.reviewed_at)}</span>}
            </div>

            <div className="intakepanel-actions">
              {saveMsg && <span className="intakepanel-savemsg">{saveMsg}</span>}
              <button className="intakepanel-btn-secondary" onClick={() => handleSave(null)} disabled={saving || !hasEdits}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {form.status !== 'reviewed' && (
                <button className="intakepanel-btn-primary" onClick={() => handleSave('reviewed')} disabled={saving}>
                  ✓ Mark as reviewed
                </button>
              )}
            </div>
          </div>

          {/* ── Investigator-only notes -- never shown to the client ── */}
          <div className="intakepanel-notes">
            <h3>Investigator Notes <span className="intakepanel-notes-sub">(never shown to the client)</span></h3>
            <div className="intakepanel-note-form">
              <textarea rows={2} placeholder="Add a note…" value={noteText} onChange={e => setNoteText(e.target.value)} />
              <button className="intakepanel-btn-secondary" onClick={handleAddNote} disabled={!noteText.trim()}>Add</button>
            </div>
            {notesLoading ? (
              <p className="intakepanel-loading">Loading notes…</p>
            ) : notes.length === 0 ? (
              <p className="intakepanel-hint">No notes yet.</p>
            ) : (
              <div className="intakepanel-notes-list">
                {notes.map(n => (
                  <div key={n.id} className="intakepanel-note">
                    <div className="intakepanel-note-body">{n.content}</div>
                    <div className="intakepanel-note-meta">
                      <span>{n.author} · {fmt(n.created_at)}</span>
                      <button onClick={() => handleDeleteNote(n.id)} title="Delete">🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
